import React, { useEffect, useRef, useState } from 'react';
import { StoredSlide } from '@/types/SlideType';
import { SlideVideoOptions } from '@/types/SlideVideoOptions';

import {
  SlideLoadingPlaceholder,
  StyledFrameImage,
  StyledSlideImage,
} from './styles';

interface SlideProps {
  contentType: string;
  filePath: string;
  videoRef: React.RefObject<HTMLVideoElement> | null;
  videoOptions?: SlideVideoOptions;
  preloadedImage?: HTMLImageElement;
  frame?: StoredSlide;
}

const RendererSlideComponent: React.FC<SlideProps> = React.memo(
  ({
    contentType,
    filePath,
    videoRef,
    videoOptions,
    preloadedImage,
    frame,
  }: SlideProps) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const imageRef = useRef<HTMLImageElement>(null);
    useEffect(() => {
      let mounted = true;
      let rafId: number;

      const checkImageRendered = () => {
        if (!imageRef.current) return;

        const { naturalWidth, naturalHeight } = imageRef.current;

        // Check if image has dimensions and is visible in DOM
        if (naturalWidth && naturalHeight && imageRef.current.complete) {
          // Use requestAnimationFrame to ensure browser has painted
          rafId = requestAnimationFrame(() => {
            if (mounted) {
              setImageLoaded(true);
            }
          });
        } else {
          rafId = requestAnimationFrame(checkImageRendered);
        }
      };

      if (imageRef.current) {
        // Start checking once the image loads
        imageRef.current.addEventListener('load', () => {
          checkImageRendered();
        });

        // Also check immediately in case image is already cached
        checkImageRendered();
      }

      return () => {
        mounted = false;
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
      };
    }, [filePath]);

    if (contentType === 'video/mp4') {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {frame && (
            <StyledFrameImage src={frame.filePath} alt="frameoverlay" />
          )}
          <video
            style={{ objectFit: 'contain', height: '100%', width: '100%' }}
            autoPlay
            loop={videoOptions?.loop ?? false}
            ref={videoRef}
          >
            <source src={filePath} type="video/mp4" />
          </video>
        </div>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {!imageLoaded && <SlideLoadingPlaceholder />}
        {frame && <StyledFrameImage src={frame.filePath} alt="frameoverlay" />}
        <StyledSlideImage
          ref={imageRef}
          src={preloadedImage?.src || filePath}
          alt="Slide"
          $loaded={imageLoaded}
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.filePath === nextProps.filePath &&
      prevProps.contentType === nextProps.contentType
    );
    // We intentionally exclude videoRef and videoOptions from the comparison
    // as they shouldn't trigger re-renders for images
  },
);

RendererSlideComponent.displayName = 'Slide';

export default RendererSlideComponent;
